import { CaseStudyContent } from '~/features/case-study/models/case-study-content';
import { isCaseStudy } from '~/features/case-study/types';
import { CaseStudyContent as CaseStudyContentV2 } from '~/features/case-study-v2/models/case-study-content';
import { isCaseStudy as isCaseStudyV2 } from '~/features/case-study-v2/types';
import { AllEnterpriseResourceContent } from '~/features/enterprise-resource/models/enterprise-resources-content';
import { RelatedResource } from '~/features/enterprise-resource/models/enterprise-video-content';
import {
  isEbookResource,
  isInfographicResource,
  isLiveEventResource,
  isReportResource,
  isVideoResource,
  isWebinarResource,
} from '~/features/enterprise-resource/types';

import { EnterpriseCenterResource } from './types';

export function convertResourcesToStandardFormat(
  resources: (AllEnterpriseResourceContent | CaseStudyContent | CaseStudyContentV2 | RelatedResource)[]
): EnterpriseCenterResource[] {
  const transformedResources: (EnterpriseCenterResource | undefined)[] = resources.map((resource) => {
    if (isEbookResource(resource)) {
      return {
        type: 'ebook',
        _id: resource._id,
        title: resource.ebookData.title,
        linkText: 'View eBook',
        linkHref: `/ebook/${resource.slug}`,
        resourceCenterImage: resource.resourceCenterImage,
        industries: resource.industries,
        knowledgeAreas: resource.knowledgeAreas,
      } as const;
    }

    if (isInfographicResource(resource)) {
      return {
        type: 'infographic',
        _id: resource._id,
        title: resource.infographicData.title,
        linkText: 'View Infographic',
        linkHref: `/infographic/${resource.slug}`,
        resourceCenterImage: resource.resourceCenterImage,
        industries: resource.industries,
        knowledgeAreas: resource.knowledgeAreas,
      } as const;
    }

    if (isReportResource(resource)) {
      return {
        type: 'report',
        _id: resource._id,
        title: resource.reportData.title,
        linkText: 'View Report',
        linkHref: `/report/${resource.slug}`,
        resourceCenterImage: resource.resourceCenterImage,
        industries: resource.industries,
        knowledgeAreas: resource.knowledgeAreas,
      } as const;
    }

    if (isWebinarResource(resource)) {
      return {
        type: 'webinar',
        _id: resource._id,
        title: resource.webinarData.title,
        linkText: 'View Webinar',
        linkHref: `/webinar/${resource.slug}`,
        resourceCenterImage: resource.resourceCenterImage,
        industries: resource.industries,
        knowledgeAreas: resource.knowledgeAreas,
      } as const;
    }

    if (isLiveEventResource(resource)) {
      return {
        type: 'live-event',
        _id: resource._id,
        title: resource.liveEventData.title,
        linkText: 'View Live Event',
        linkHref: `/event/${resource.slug}`,
        resourceCenterImage: resource.resourceCenterImage,
        industries: resource.industries,
        knowledgeAreas: resource.knowledgeAreas,
      } as const;
    }

    if (isCaseStudy(resource)) {
      return {
        type: 'case-study',
        _id: resource._id,
        title: resource.title,
        linkText: 'View Case Study',
        linkHref: `/enterprise/case-study/${resource.slug}`,
        resourceCenterImage: resource.resourceCenterImage,
        industries: resource.industries,
        knowledgeAreas: resource.knowledgeAreas,
      } as const;
    }

    if (isCaseStudyV2(resource)) {
      return {
        type: 'case-study',
        _id: resource._id,
        title: resource.title,
        linkText: 'View Case Study',
        linkHref: `/enterprise/case-study/${resource.slug}`,
        resourceCenterImage: resource.resourceCenterImage,
        industries: resource.industries,
        knowledgeAreas: resource.knowledgeAreas,
      } as const;
    }

    if (isVideoResource(resource)) {
      return {
        type: 'video',
        _id: resource._id,
        title: resource.videoData.title,
        linkText: 'View Video',
        linkHref: `/video/${resource.slug}`,
        resourceCenterImage: resource.resourceCenterImage,
        industries: resource.industries,
        knowledgeAreas: resource.knowledgeAreas,
      } as const;
    }
  });

  return transformedResources.filter((resource): resource is EnterpriseCenterResource => resource !== undefined);
}
